import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, DmsTextField, intl, fetchData } from "dmsbasereact";
import {
    Box,
    useMediaQuery,
    useTheme,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Chip,
    Typography,
    CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";

const InstitutionPartnerGroupFormModal = ({
    isVisible,
    onClose,
    onSave = null,
    group = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isEditMode = Boolean(group);

    const [formName, setFormName] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formCnpjRoot, setFormCnpjRoot] = useState("");
    const [formMembers, setFormMembers] = useState([]);
    const [availableInstitutions, setAvailableInstitutions] = useState([]);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadAvailableInstitutions = useCallback(async () => {
        setLoadingInstitutions(true);
        try {
            const response = await fetchData("/institution_partner_group/available-institutions", "GET", true);
            const data = response?.data?.data || response?.data || [];
            setAvailableInstitutions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load available institutions:", error);
            setAvailableInstitutions([]);
        } finally {
            setLoadingInstitutions(false);
        }
    }, []);

    useEffect(() => {
        if (isVisible) {
            loadAvailableInstitutions();
            if (group) {
                setFormName(group.name || "");
                setFormDescription(group.description || "");
                setFormCnpjRoot(group.cnpj_root || "");
                setFormMembers(group.members?.map((m) => m.organization_branch_pk) || []);
            } else {
                setFormName("");
                setFormDescription("");
                setFormCnpjRoot("");
                setFormMembers([]);
            }
        }
    }, [isVisible, group, loadAvailableInstitutions]);

    const handleSave = async () => {
        if (!formName.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("GroupNameRequired").d("Group name is required."),
            });
            return;
        }

        const confirmResult = await Swal.fire({
            icon: "question",
            title: intl.get("ConfirmAction").d("Confirm Action"),
            text: isEditMode
                ? intl.get("ConfirmUpdatePartnerGroup").d("Are you sure you want to update this partner group?")
                : intl.get("ConfirmCreatePartnerGroup").d("Are you sure you want to create this partner group?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formName.trim(),
                description: formDescription.trim() || null,
                cnpj_root: formCnpjRoot.trim() || null,
                members: formMembers.map((pk) => ({ organization_branch_pk: pk })),
            };

            let response;
            if (isEditMode) {
                response = await fetchData(`/institution_partner_group/${group.pk}`, "PUT", true, payload);
            } else {
                response = await fetchData("/institution_partner_group", "POST", true, payload);
            }

            if (response.status >= 200 && response.status < 300) {
                await Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: isEditMode
                        ? intl.get("PartnerGroupUpdatedSuccessfully").d("Partner group updated successfully")
                        : intl.get("PartnerGroupCreatedSuccessfully").d("Partner group created successfully"),
                });
                if (onSave) onSave();
                onClose();
            } else {
                const errorMessage = response.data?.detail || intl.get("FailedToSavePartnerGroup").d("Failed to save partner group");
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: errorMessage,
                });
            }
        } catch (error) {
            console.error("Failed to save partner group:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToSavePartnerGroup").d("Failed to save partner group"),
            });
        } finally {
            setSaving(false);
        }
    };

    const title = isEditMode
        ? intl.get("EditPartnerGroup").d("Edit Partner Group")
        : intl.get("CreatePartnerGroup").d("Create Partner Group");

    const isFormValid = formName.trim().length > 0;

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={title} onClose={onClose} />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("BasicInfo").d("Basic Information")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="name"
                                label={intl.get("GroupName").d("Group Name")}
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="description"
                                label={intl.get("Description").d("Description")}
                                value={formDescription}
                                onChange={(e) => setFormDescription(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="cnpj_root"
                                label={intl.get("CNPJRoot").d("CNPJ Root")}
                                value={formCnpjRoot}
                                onChange={(e) => setFormCnpjRoot(e.target.value.replace(/\D/g, "").slice(0, 8))}
                                fullWidth
                                placeholder="12345678"
                                helperText={intl.get("CNPJRootHelp").d("First 8 digits of CNPJ for auto-suggestion")}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("MemberInstitutions").d("Member Institutions")}
                    </Typography>
                    <FormControl fullWidth disabled={loadingInstitutions}>
                        <InputLabel>{intl.get("SelectInstitutions").d("Select Institutions")}</InputLabel>
                        <Select
                            multiple
                            value={formMembers}
                            label={intl.get("SelectInstitutions").d("Select Institutions")}
                            onChange={(e) => setFormMembers(e.target.value)}
                            input={<OutlinedInput label={intl.get("SelectInstitutions").d("Select Institutions")} />}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        maxWidth: '90vw',
                                    },
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                            }}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((pk) => {
                                        const institution = availableInstitutions.find((i) => i.pk === pk);
                                        return (
                                            <Chip
                                                key={pk}
                                                label={institution?.name || pk}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {loadingInstitutions ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    {intl.get("Loading").d("Loading...")}
                                </MenuItem>
                            ) : (
                                availableInstitutions.map((institution) => (
                                    <MenuItem key={institution.pk} value={institution.pk}>
                                        <Checkbox checked={formMembers.indexOf(institution.pk) > -1} />
                                        <ListItemText
                                            primary={institution.name}
                                            secondary={institution.cnpj}
                                        />
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={onClose}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                        onClick={handleSave}
                        fullWidth={isMobile}
                        disabled={!isFormValid || saving}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

InstitutionPartnerGroupFormModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    group: PropTypes.object,
};

export { InstitutionPartnerGroupFormModal };
